import type { ReportEvent } from '@/core/logger';
import type { CommonPackage } from './common';
import type { v3Env, SendCallback } from '@/config/types';

import { BatchLog as BaseLogger} from '@/core/logger';
import { getCookie } from '@/util/cookie';
import { generateDID } from '@/util/uuid';
import { parseQueryString, enableAsyncGzip } from '@/util';
import sendData from '@/io';
import { get } from '@/io/xhr';
import { dataTrackHost, overseaDataTrackHost, buildUrl, LOGGER_SGP, LOGGER, PROD } from '@/config/apis';
import { getReportUrl } from '@/util/test';
import { loadScript } from '@/util/loader';
import type { ResponseType } from '@/io/types';

interface WebloggerSwitch {
    // 将log切换发送到该地址
    reportHost: string;
    // log测试前端页面地址
    loggerHost: string;
    // 本次logger测试的sessionId
    loggerSessionId: string;
    handshakeApi?: string;
}
const SWICTH = 'weblogger_switch';
/**
 * 端内环境根据cookie值切换发送地址
 */
const getCookieReportUrl = () => {
    try {
        const webloggerSwitch = getCookie(SWICTH) ||
            typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SWICTH);
        if (!webloggerSwitch) return;
        const { loggerSessionId, reportHost, loggerHost, handshakeApi }: WebloggerSwitch = JSON.parse(webloggerSwitch);
        // 若cookie内存在loggerSessionId，则会自动切换发送路径到测试地址，否则返回空，代表不切换地址
        if (!loggerSessionId) {
            return '';
        }
        get(handshakeApi || `${reportHost}/${loggerSessionId}`);
        return getReportUrl(loggerHost, reportHost, loggerSessionId);
    } catch (err) {
        return '';
    }
};

/**
 * env 为 logger 时上报的 url
 */
const getLoggerReportUrl = (did: string = generateDID(), isOversea = false) => {
    const url = isOversea ? overseaDataTrackHost : dataTrackHost;
    get(`${url}/rest/${did}`);
    return getReportUrl(url, `${url}/rest`, did);
};

/**
 * 端外H5 or pc页面根据当前的url参数切换发送地址
 */
const getWebPageReportUrl = (url: string = location.href) => {
    // const { storage, io } = Global.adapter;
    const queryIndex = url.lastIndexOf('?');
    if (queryIndex === -1) {
        return '';
    }
    const queryString = url.slice(queryIndex + 1);
    const { webloggerSwitch } = parseQueryString(queryString);
    if (!webloggerSwitch) {
        return '';
    }
    try {
        const data = decodeURIComponent(webloggerSwitch);
        const { loggerSessionId, reportHost, loggerHost, handshakeApi } = JSON.parse(data);
        if (!loggerSessionId) {
            return '';
        }
        // 存到 sessionStorage 中使同域名页面都可以调试
        // storage.setSession(SWICTH, data);
        // io.get({url: handshakeApi || `${reportHost}/${loggerSessionId}`});
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(SWICTH, data);
        }
        get(handshakeApi || `${reportHost}/${loggerSessionId}`);
        return getReportUrl(loggerHost, reportHost, loggerSessionId);
    } catch (err) {
        return '';
    }
};

const getTemporaryLoggerUrl = (proto?: 'v2' | 'v3') => {
    const url = getCookieReportUrl() || getWebPageReportUrl();
    if (url) {
        return buildUrl(url, false, proto);
    }
};

export const RESPONSE_SAMPLING_STORAGE_KEY = 'RESPONSE_SAMPLING_STORAGE_KEY';

export default class Logger extends BaseLogger {
    static Gzipper?: any;
    declare commonPackage: CommonPackage;
    enableAsyncGzip: boolean | undefined;
    override baseSendData = this.sendLog;
    // 服务端控制的采样发送，可以发送合法时间，当前时间戳大于这个时间戳，才可以发送
    sendEffectiveTime: undefined | number;
    // 采样存储 key，作为实例化的一个键值，避免多实例互相干扰
    constructor(config: any, commonPackage: CommonPackage) {
        super(config, commonPackage);
        this.getResponseSamplingStorage();
    }
    // 获取 storage 判断是否有过期时间
    async getResponseSamplingStorage() {
        try {
            // 直接异步获取 localstorage 信息。保证初始化 weblogger 性能。
            // 因为 sendImmediately 也是微任务发送，所以获取本地存储信息，是可以保证在发送 pv 之前,获取到采样信息
            await Promise.resolve();
            const storageEffectiveTime = Number(localStorage.getItem(this.responseSamplingStorageKey));
            if (storageEffectiveTime && (Date.now() < storageEffectiveTime)) {
                this.sendEffectiveTime = storageEffectiveTime;
            } else if (storageEffectiveTime) {
                // 如果采样已经过期，清除 storage
                localStorage.removeItem(this.responseSamplingStorageKey);
            }
        } catch (e) {
            console.error(e);
        }
    }
    /**
     * 返回 true 代表不发送埋点
     * @param log
     * @param immediate
     * @param callback
     */
    isResponseSampling(): boolean {
        return !!(this.sendEffectiveTime && (Date.now() < this.sendEffectiveTime));
    }
    // 埋点返回结果处理
    setResponseSamplingConfig(responsePromise?: Promise<string | undefined>) {
        if (this.isSetSamplingResult) { return; }
        this.isSetSamplingResult = true;
        responsePromise?.then(res => {
            let response: ResponseType;
            try {
                response = res && JSON.parse(res);
            } catch (e) {
                response = {
                    result: 1,
                    hostName: '',
                };
            }
            const expireSeconds = response?.expireSeconds;
            if (expireSeconds) {
                const effectiveTime = Date.now() + expireSeconds * 1000;
                this.sendEffectiveTime = effectiveTime;
                localStorage.setItem(this.responseSamplingStorageKey, String(effectiveTime));
            }
        });
    }
    /**
     * 根据配置生成上报的 url 地址
     */
     override updateUrls() {
        const { env = PROD, logger, proto, isBridge, disableCompress } = this.config;
        const url = getTemporaryLoggerUrl(proto);
        if (url) {
            this.url = url;
            this.isDebug = true;
        } else if (logger || env === LOGGER || env === LOGGER_SGP) {
            const loggerEnv = getLoggerReportUrl(this.commonPackage.identity_package.device_id, env === LOGGER_SGP);
            this.url = buildUrl(loggerEnv, false, proto);
            this.isDebug = true;
        } else if (env && /^(https?:)?\/\//.test(env)) {
            this.url = env;
        } else {
            this.isDebug = env !== PROD;
            this.url = buildUrl(env as v3Env, false, proto);
            this.radarUrl = buildUrl(env as v3Env, true, proto);
        }
        this.enableAsyncGzip = !disableCompress // 用户没有配置禁止压缩
            && proto === 'v3' // 必须是 v3 上报, v2 虽然压缩上报接口虽然没报错，但是服务端不做解析。
            && !isBridge // 非桥接上报
            && enableAsyncGzip() // 平台兼容性
            && env === PROD // 必须在 prod 环境
            && !url // 非埋点抓包查找
            && !logger; // 非埋点抓包查找
        this.enableAsyncGzip && loadScript('__GZIPER_URL__');
        this.formatUrl();
    }
    sendLog(data: {url: string, data: any, [key: string]: any}, callback?: (error?: any) => void): void {
         const { forceSendBeacon } = this.config;
        if (!this.isV2) {
            //  根据埋点接口判断是否需要采样
            if (this.isResponseSampling()) {
                return;
            }
        }
        if (this.drained || !this.enableAsyncGzip || !this.sendGzip(data, callback)) {
            this.setResponseSamplingConfig(
                sendData({ ...data, forceSendBeacon }, callback),
            );
        }
    }
    /**
     *
     * @param data
     * @param callback weblogger 系统级错误回调，会清除缓存的发送错误的日志。
     */
    sendGzip(data: {url: string, data: any, [key: string]: any}, callback?: (error?: any) => void) {
        if (!Logger.Gzipper?.sendData) return false;
        const workerCallback = (e: any) => {
            if (e) {
                this.setResponseSamplingConfig(
                    sendData(data, callback),
                );
            }
        };
        // 如果发送压缩代码成功，会返回一个 promise
        const promise = Logger.Gzipper.sendData(data, workerCallback);
        this.setResponseSamplingConfig(promise);
        return !!promise;
    }
    /**
     * 埋点发送前获取 common_package 信息
     * @param data
     * @returns
     */
    override getCommonPackageJSON() {
        return this.commonPackage.toJSON();
    }

    override send(data: ReportEvent, immediate = false, callback?: SendCallback) {
        // v2 是每个 log 都有 common_package
        if (this.isV2) {
            // v2 log 的自增 id 是存在 common 信息中
            this.commonPackage.setAdditionalSeqIdPackage((data as any).getEventType());
            data.common_package = this.getCommonPackageJSON();
        }
        super.send(data, immediate, callback);
    }
}
